import * as mapboxgl from 'mapbox-gl';
import { GeoJSONSource } from 'mapbox-gl';
import Mapbox from 'index.vue';
import { BaseLocationsLayer } from './baseLocationsLayer';
import { localeIncludes } from 'locale-includes';
import * as turf from '@turf/turf';
import destination from '@turf/destination';
import { BaseFeatureLayer } from './baseFeatureLayer';

export class LocationsLayer {

    protected mapboxInstance: InstanceType<typeof Mapbox>;
    private locationCirclesLayer;
    private locationCircleLabelsLayer;
    private locationsLayer;
    constructor(mapboxInstance: InstanceType<typeof Mapbox>) {
        this.mapboxInstance = mapboxInstance;
        
        this.locationCirclesLayer = new BaseFeatureLayer(mapboxInstance, 'top', 'locations-circles-layer', 'locations-circles-source', 'line', {}, {
            'line-color': '#000000',
            'line-width': 1
        });
        this.locationCircleLabelsLayer = new BaseFeatureLayer(mapboxInstance, 'top', 'locations-circle-labels-layer', 'locations-circle-labels-source', 'symbol', {
            'text-field': ['get', 'label'],
            'text-size': 14,
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-ignore-placement': false,
          //  'text-offset': [0, -1.2] 
        }, {
            'text-color': '#fff',
            'text-halo-color': '#000',
            'text-halo-width': 100
        });
        this.locationsLayer = new BaseLocationsLayer(mapboxInstance, '', 'locations-layer', 'locations-source', 'symbol', {
            'icon-image':  ['get', 'url'],
            'icon-size': ['/', ['get', 'size'], 1],
            'icon-allow-overlap': true,
            'icon-anchor': 'top-left',
            'icon-offset':
                [-20, ['+', -20, ['get', 'offset']]
                ],

            'symbol-sort-key': ['get', 'zIndex'],
        }, { 'icon-opacity': ['case', ['get', 'inFilter'], 1, 0.4]  }, true);
        this.mapboxInstance.map.on('click', 'locations-layer', (e: any) => {
            e.features.sort((a : any, b : any) => (b.properties.zIndex || 0) - (a.properties.zIndex || 0));
            this.mapboxInstance.$emit('locationLeftClick', { x: e.point.x, y: e.point.y, latitude: e.lngLat.lat, longitude: e.lngLat.lng, location: JSON.parse(e.features[0].properties.location) });
        });
        this.mapboxInstance.map.on('contextmenu', 'locations-layer', (e: any) => {
            e.features.sort((a: any, b: any) => (b.properties.zIndex || 0) - (a.properties.zIndex || 0));
            this.mapboxInstance.$emit('locationRightClick', { x: e.point.x, y: e.point.y, latitude: e.lngLat.lat, longitude: e.lngLat.lng, location: JSON.parse(e.features[0].properties.location) });
        });
    }
    
    public SelectLocation = async (oldLocationId: any, newLocationId: any) => {
        if (this.mapboxInstance.map.getSource('locations-source')) {
            const source = this.mapboxInstance.map.getSource('locations-source') as GeoJSONSource;
            const geojson = (source as any)._data as GeoJSON.FeatureCollection;
            if (oldLocationId) this.updateIconForLocation(this.features, oldLocationId, false);
            if (newLocationId) this.updateIconForLocation(this.features, newLocationId, true);
            this.locationsLayer.SetFeatures(this.features);
        }
    }
    private updateIconForLocation = (features: any[], locationId: number | null, isSelected: boolean) => {
        const feature = features.find((f: any) => f.properties.location?.id === locationId);
        if (feature) {
            const iconProps = this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, feature.properties.location, isSelected);
            feature.properties = { ...feature.properties, ...iconProps };
        }
    };
   
    private features: any[] = [];
    private lastModelId: any = null;



    public AddCircles = async (location: any) => {
        if (location == null) {
            this.locationCirclesLayer.SetFeatures([]);
            this.locationCircleLabelsLayer.SetFeatures([]);

        }
        else {
            const circles = this.mapboxInstance.model.mapRanges.map(function (mapRange: any) {
                return turf.circle([location.lng, location.lat] as [number, number], mapRange, { steps: 64, units: 'meters' });
            });
            const labels = this.mapboxInstance.model.mapRanges.map(function (mapRange: any) {
                const topPoint = destination([location.lng, location.lat] as [number, number], mapRange, 0, { units: 'meters' });
                return {
                    type: 'Feature',
                    geometry: topPoint.geometry,
                    properties: {
                        label: mapRange + 'M'
                    }
                };
            });
            this.locationCirclesLayer.SetFeatures(circles);
            this.locationCircleLabelsLayer.SetFeatures(labels);
        }
        
    }



    public DataChanged = async (leftPadding: number = 0, rightPadding: number = 0, maintainZoomAndPosition = false) => {
        const modelId = this.mapboxInstance.locations.length == 0 ? null : this.mapboxInstance.locations[0].modelId;


        const uniqueUrls = new Set<string>();
        this.mapboxInstance.locations.forEach((location: any) => {
            const normalUrl = this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, location, false).url;
            const selectedUrl = this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, location, true).url;
            uniqueUrls.add(normalUrl);
            uniqueUrls.add(selectedUrl);
        });
        const loadImagePromises = this.locationsLayer.LoadImages(uniqueUrls);

        
        const locationsInFilter: any[] = [];
        this.features = this.mapboxInstance.locations.filter((location: any) => location.visibility !== 2).map((location: any) => {
            const inFilter = this.mapboxInstance.filter != null ? localeIncludes(location.name, this.mapboxInstance.filter, { usage: "search", sensitivity: "base" }) || (location.locationGroup != null && localeIncludes(location.locationGroup, this.mapboxInstance.filter, { usage: "search", sensitivity: "base" })) : true;
            if (inFilter) locationsInFilter.push(location);
            return {
                
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [location.lng, location.lat] as [number, number],
                },
                properties: {
                    ...this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, location, location.id == this.mapboxInstance.location?.id, false),
                    location: location,
                    title: location.name,
                    inFilter: inFilter
                }
            }
        });
        
        await Promise.all(loadImagePromises);
        this.locationsLayer.SetFeatures(this.features);

        if (locationsInFilter.length == 1) this.mapboxInstance.$emit('locationLeftClick', { x: 0, y: 0, latitude: 0, longitude: 0, location: locationsInFilter[0] });

            

        
        const bounds = new mapboxgl.LngLatBounds();
        const boundsChanged = new mapboxgl.LngLatBounds();
        this.mapboxInstance.locations.filter(function (location: any) { return location.visibility != 2 }).map(function (location: any) {
            if (location.changeType > 0) boundsChanged.extend([location.lng, location.lat]);
            bounds.extend([location.lng, location.lat]);
        });
        if (!this.mapboxInstance.report && !maintainZoomAndPosition) {
            if (this.mapboxInstance.locations != null && this.mapboxInstance.locations.length > 0 && this.lastModelId != modelId) {
                this.mapboxInstance.map.fitBounds(bounds, {
                    padding: { top: 30, right: rightPadding + 60, left: leftPadding + 60, bottom: 30 },
                    maxZoom: 12,
                    duration: 1000, // Smooth animation duration in milliseconds
                });
            }
            else if (!boundsChanged.isEmpty()) {
                this.mapboxInstance.map.fitBounds(boundsChanged, {
                    padding: { top: 30, right: rightPadding + 60, left: leftPadding + 60, bottom: 30 },
                    maxZoom: 12,
                    duration: 1000, // Smooth animation duration in milliseconds
                });
            }
        }
        this.lastModelId = modelId;
        
    };
}   
